import { motion } from 'framer-motion';
import { useState } from 'react';

const AuditApplicationSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    website: '',
    revenue: '',
    challenge: '',
    referral: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'challenge') {
      if (value.length <= 500) {
        setFormData((prev) => ({ ...prev, [name]: value }));
        setCharCount(value.length);
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.company || !formData.revenue || !formData.challenge) {
      return;
    }

    setIsSubmitting(true);

    await new Promise((resolve) => setTimeout(resolve, 1500));

    setSubmitSuccess(true);
    setIsSubmitting(false);
    
    setFormData({
      name: '',
      email: '',
      company: '',
      website: '',
      revenue: '',
      challenge: '',
      referral: '',
    });
    setCharCount(0);
  };

  return (
    <section className="relative py-24 lg:py-32 overflow-hidden bg-[#080808]">
      {/* Ambient glow - desktop only */}
      <div className="hidden lg:block absolute inset-0 pointer-events-none">
        <motion.div
          animate={{ scale: [1, 1.08, 1], opacity: [0.12, 0.2, 0.12] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-accent-blue blur-[160px]"
        />
      </div>

      {/* Minimal static glow for mobile */}
      <div className="lg:hidden absolute inset-0 pointer-events-none">
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[200px] h-[200px] rounded-full bg-accent-blue blur-[70px] opacity-10" />
      </div>

      <div className="max-w-[750px] mx-auto px-5 lg:px-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.8 }}
          className="text-center mb-10 lg:mb-12"
        >
          <h2 className="text-[32px] lg:text-5xl font-bold text-white mb-4 lg:mb-5 leading-tight">
            Apply for a Force Multiplier Audit
          </h2>
          <p className="text-[15px] lg:text-lg text-gray-400 leading-relaxed">
            Identify the single bottleneck holding your company back from its next level of growth.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-gradient-to-br from-[#0f1419]/80 to-[#0a0e13]/80 backdrop-blur-xl border border-white/10 rounded-2xl lg:rounded-3xl p-6 lg:p-10 relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-accent-blue/5 to-transparent opacity-50" />
          
          <form onSubmit={handleSubmit} className="relative z-10 space-y-5 lg:space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-white mb-2">
                Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 lg:px-5 py-3 lg:py-3.5 bg-[#0a0e13] border border-white/10 rounded-lg lg:rounded-xl text-white text-[15px] lg:text-base placeholder-gray-500 focus:outline-none focus:border-accent-blue/50 focus:ring-2 focus:ring-accent-blue/20 transition-all duration-200 min-h-[48px]"
                placeholder="Your full name"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-white mb-2">
                Email <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 lg:px-5 py-3 lg:py-3.5 bg-[#0a0e13] border border-white/10 rounded-lg lg:rounded-xl text-white text-[15px] lg:text-base placeholder-gray-500 focus:outline-none focus:border-accent-blue/50 focus:ring-2 focus:ring-accent-blue/20 transition-all duration-200 min-h-[48px]"
                placeholder="your@email.com"
              />
            </div>

            {/* Company */}
            <div>
              <label htmlFor="company" className="block text-sm font-semibold text-white mb-2">
                Company <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                className="w-full px-4 lg:px-5 py-3 lg:py-3.5 bg-[#0a0e13] border border-white/10 rounded-lg lg:rounded-xl text-white text-[15px] lg:text-base placeholder-gray-500 focus:outline-none focus:border-accent-blue/50 focus:ring-2 focus:ring-accent-blue/20 transition-all duration-200 min-h-[48px]"
                placeholder="Company name"
              />
            </div>

            {/* Website */}
            <div>
              <label htmlFor="website" className="block text-sm font-semibold text-white mb-2">
                Website
              </label>
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                className="w-full px-4 lg:px-5 py-3 lg:py-3.5 bg-[#0a0e13] border border-white/10 rounded-lg lg:rounded-xl text-white text-[15px] lg:text-base placeholder-gray-500 focus:outline-none focus:border-accent-blue/50 focus:ring-2 focus:ring-accent-blue/20 transition-all duration-200 min-h-[48px]"
                placeholder="https://yourcompany.com"
              />
            </div>

            {/* Revenue Range */}
            <div>
              <label htmlFor="revenue" className="block text-sm font-semibold text-white mb-2">
                Current Revenue Range <span className="text-red-400">*</span>
              </label>
              <select
                id="revenue"
                name="revenue"
                value={formData.revenue}
                onChange={handleChange}
                required
                className="w-full px-4 lg:px-5 py-3 lg:py-3.5 bg-[#0a0e13] border border-white/10 rounded-lg lg:rounded-xl text-white text-[15px] lg:text-base focus:outline-none focus:border-accent-blue/50 focus:ring-2 focus:ring-accent-blue/20 transition-all duration-200 min-h-[48px] cursor-pointer"
              >
                <option value="" disabled>Select revenue range</option>
                <option value="pre-revenue">Pre-revenue</option>
                <option value="0-1m">$0 – $1M</option>
                <option value="1m-10m">$1M – $10M</option>
                <option value="10m-50m">$10M – $50M</option>
                <option value="50m+">$50M+</option>
              </select>
            </div>

            {/* Primary Challenge */}
            <div>
              <label htmlFor="challenge" className="block text-sm font-semibold text-white mb-2">
                Primary Growth Challenge <span className="text-red-400">*</span>
              </label>
              <textarea
                id="challenge"
                name="challenge"
                value={formData.challenge}
                onChange={handleChange}
                required
                rows={4}
                maxLength={500}
                className="w-full px-4 lg:px-5 py-3 lg:py-3.5 bg-[#0a0e13] border border-white/10 rounded-lg lg:rounded-xl text-white text-[15px] lg:text-base placeholder-gray-500 focus:outline-none focus:border-accent-blue/50 focus:ring-2 focus:ring-accent-blue/20 transition-all duration-200 resize-none"
                placeholder="Describe the main bottleneck or challenge limiting your growth..."
              />
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-gray-500">Maximum 500 characters</span>
                <span className={`text-xs font-medium ${charCount > 450 ? 'text-yellow-400' : 'text-gray-500'}`}>
                  {charCount}/500
                </span>
              </div>
            </div>

            {/* Referral Source */}
            <div>
              <label htmlFor="referral" className="block text-sm font-semibold text-white mb-2">
                How did you hear about Force Multiplier?
              </label>
              <input
                type="text"
                id="referral"
                name="referral"
                value={formData.referral}
                onChange={handleChange}
                className="w-full px-4 lg:px-5 py-3 lg:py-3.5 bg-[#0a0e13] border border-white/10 rounded-lg lg:rounded-xl text-white text-[15px] lg:text-base placeholder-gray-500 focus:outline-none focus:border-accent-blue/50 focus:ring-2 focus:ring-accent-blue/20 transition-all duration-200 min-h-[48px]"
                placeholder="Referral, search, social media, etc."
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || submitSuccess}
              className="w-full px-6 py-4 bg-accent-blue text-white text-[15px] lg:text-base font-semibold rounded-full hover:brightness-125 active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap min-h-[52px] flex items-center justify-center cursor-pointer"
            >
              {isSubmitting ? 'Submitting...' : submitSuccess ? 'Application Submitted!' : 'Apply for Audit'}
            </button>

            {/* Success Message */}
            {submitSuccess && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center p-4 bg-green-500/10 border border-green-500/30 rounded-lg lg:rounded-xl"
              >
                <p className="text-sm lg:text-base text-green-400 font-medium">
                  Thank you! We've received your application and will review it shortly.
                </p>
              </motion.div>
            )}

            {/* Trust Message */}
            <p className="text-center text-[13px] lg:text-sm text-gray-500 leading-relaxed">
              We review every submission personally. If there is a clear bottleneck we can solve, we will reach out.
            </p>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default AuditApplicationSection;